import type { ResolvedTheme } from '../../types/ThemeType';
import type { AccentType } from '../../types/AccentType';

export type AccentPalette = {
  /** Primary tint for text, icons, links */
  accent: string;
  /** Hover fills, badge backgrounds */
  muted: string;
  /** Selected rows, subtle highlights */
  subtle: string;
  /** Text on solid accent controls */
  onAccent: string;
  /** Primary button background */
  buttonBg: string;
  buttonHover: string;
  buttonActive: string;
  /** Outline / ghost hover */
  outlineHover: string;
  focusRing: string;
  selection: string;
};

export type AccentPaletteSet = Record<ResolvedTheme, AccentPalette>;

/**
 * Workspace accent palettes — softened for Apple-like materials.
 * Light: solid fills; dark: tinted fills with brighter accent labels.
 */
export const accentPalettes: Record<AccentType, AccentPaletteSet> = {
  brick: {
    light: {
      accent: '#D95430',
      muted: 'rgba(217, 84, 48, 0.14)',
      subtle: 'rgba(217, 84, 48, 0.08)',
      onAccent: '#FFFFFF',
      buttonBg: '#D95430',
      buttonHover: '#C44A28',
      buttonActive: '#B04223',
      outlineHover: 'rgba(217, 84, 48, 0.10)',
      focusRing: 'rgba(217, 84, 48, 0.40)',
      selection: 'rgba(217, 84, 48, 0.20)',
    },
    dark: {
      accent: '#FF7A52',
      muted: 'rgba(255, 122, 82, 0.22)',
      subtle: 'rgba(255, 122, 82, 0.12)',
      onAccent: '#FFFFFF',
      buttonBg: 'rgba(255, 122, 82, 0.32)',
      buttonHover: 'rgba(255, 122, 82, 0.42)',
      buttonActive: 'rgba(255, 122, 82, 0.52)',
      outlineHover: 'rgba(255, 122, 82, 0.16)',
      focusRing: 'rgba(255, 122, 82, 0.45)',
      selection: 'rgba(255, 122, 82, 0.28)',
    },
  },
  sakura: {
    light: {
      accent: '#DE5E96',
      muted: 'rgba(222, 94, 150, 0.14)',
      subtle: 'rgba(222, 94, 150, 0.08)',
      onAccent: '#FFFFFF',
      buttonBg: '#DE5E96',
      buttonHover: '#CF5489',
      buttonActive: '#BF4B7D',
      outlineHover: 'rgba(222, 94, 150, 0.10)',
      focusRing: 'rgba(222, 94, 150, 0.40)',
      selection: 'rgba(222, 94, 150, 0.20)',
    },
    dark: {
      accent: '#FF8FC4',
      muted: 'rgba(255, 143, 196, 0.22)',
      subtle: 'rgba(255, 143, 196, 0.12)',
      onAccent: '#FFFFFF',
      buttonBg: 'rgba(255, 143, 196, 0.30)',
      buttonHover: 'rgba(255, 143, 196, 0.40)',
      buttonActive: 'rgba(255, 143, 196, 0.50)',
      outlineHover: 'rgba(255, 143, 196, 0.16)',
      focusRing: 'rgba(255, 143, 196, 0.45)',
      selection: 'rgba(255, 143, 196, 0.28)',
    },
  },
  emerald: {
    light: {
      accent: '#2DAF7A',
      muted: 'rgba(45, 175, 122, 0.14)',
      subtle: 'rgba(45, 175, 122, 0.08)',
      onAccent: '#FFFFFF',
      buttonBg: '#2DAF7A',
      buttonHover: '#289E6E',
      buttonActive: '#238C62',
      outlineHover: 'rgba(45, 175, 122, 0.10)',
      focusRing: 'rgba(45, 175, 122, 0.40)',
      selection: 'rgba(45, 175, 122, 0.20)',
    },
    dark: {
      accent: '#45D99A',
      muted: 'rgba(69, 217, 154, 0.22)',
      subtle: 'rgba(69, 217, 154, 0.12)',
      onAccent: '#FFFFFF',
      buttonBg: 'rgba(69, 217, 154, 0.28)',
      buttonHover: 'rgba(69, 217, 154, 0.38)',
      buttonActive: 'rgba(69, 217, 154, 0.48)',
      outlineHover: 'rgba(69, 217, 154, 0.16)',
      focusRing: 'rgba(69, 217, 154, 0.45)',
      selection: 'rgba(69, 217, 154, 0.28)',
    },
  },
  royal: {
    light: {
      accent: '#5B6BD5',
      muted: 'rgba(91, 107, 213, 0.14)',
      subtle: 'rgba(91, 107, 213, 0.08)',
      onAccent: '#FFFFFF',
      buttonBg: '#5B6BD5',
      buttonHover: '#515FC4',
      buttonActive: '#4755B2',
      outlineHover: 'rgba(91, 107, 213, 0.10)',
      focusRing: 'rgba(91, 107, 213, 0.40)',
      selection: 'rgba(91, 107, 213, 0.20)',
    },
    dark: {
      accent: '#8B98FF',
      muted: 'rgba(139, 152, 255, 0.22)',
      subtle: 'rgba(139, 152, 255, 0.12)',
      onAccent: '#FFFFFF',
      buttonBg: 'rgba(139, 152, 255, 0.30)',
      buttonHover: 'rgba(139, 152, 255, 0.40)',
      buttonActive: 'rgba(139, 152, 255, 0.50)',
      outlineHover: 'rgba(139, 152, 255, 0.16)',
      focusRing: 'rgba(139, 152, 255, 0.45)',
      selection: 'rgba(139, 152, 255, 0.28)',
    },
  },
};

export const getAccentPalette = (
  accent: AccentType,
  theme: ResolvedTheme
): AccentPalette => accentPalettes[accent][theme];

export const getAccentColor = (
  accent: AccentType,
  theme: ResolvedTheme
): string => getAccentPalette(accent, theme).accent;
